import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { Backdrop, Title, Kicker } from "../components";
import { COLORS, FONT, NEUTRAL, SAFE } from "../theme";

// Mini-tablero Kanban dentro de un "laptop".
const Laptop: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;
  const op = interpolate(f, [0, 24], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const y = interpolate(f, [0, 26], [40, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) });
  const cols = [
    { n: "Puerto", c: 3, col: COLORS.blueLight },
    { n: "PDI", c: 5, col: COLORS.blueLight },
    { n: "Bodega", c: 2, col: COLORS.purpleLight },
    { n: "Dealers", c: 4, col: COLORS.purpleLight },
    { n: "Entrega", c: 1, col: COLORS.greenLight },
  ];
  return (
    <div style={{ opacity: op, translate: `0px ${y}px` }}>
      <div
        style={{
          width: 940,
          height: 560,
          borderRadius: 22,
          background: "#0C1626",
          border: `10px solid #1B2A3E`,
          boxShadow: "0 40px 100px rgba(0,0,0,0.5)",
          padding: 34,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26 }}>
          <div style={{ width: 14, height: 14, borderRadius: 14, background: "#E05A45" }} />
          <div style={{ width: 14, height: 14, borderRadius: 14, background: COLORS.amber }} />
          <div style={{ width: 14, height: 14, borderRadius: 14, background: COLORS.greenLight }} />
          <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 24, color: COLORS.sub, marginLeft: 14 }}>
            Tablero de logística
          </div>
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          {cols.map((c, i) => {
            const grow = interpolate(f, [20 + i * 8, 40 + i * 8], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) });
            return (
              <div key={i} style={{ flex: 1 }}>
                <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 20, color: COLORS.sub, marginBottom: 12 }}>
                  {c.n}
                </div>
                <div
                  style={{
                    borderRadius: 14,
                    border: `1px solid ${NEUTRAL.line}`,
                    background: NEUTRAL.glassBg,
                    height: 340,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {Array.from({ length: c.c }).map((_, k) => (
                    <div
                      key={k}
                      style={{
                        height: 40,
                        borderRadius: 9,
                        background: `${c.col}22`,
                        border: `1px solid ${c.col}44`,
                        scale: String(k < c.c * grow ? 1 : 0),
                        transformOrigin: "top",
                      }}
                    />
                  ))}
                  <div style={{ marginTop: "auto", fontFamily: FONT, fontWeight: 800, fontSize: 34, color: c.col }}>
                    {c.c}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ width: 1040, height: 22, marginLeft: -50, borderRadius: "0 0 16px 16px", background: "#16273B" }} />
    </div>
  );
};

// Teléfono con escáner QR.
const ScanPhone: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;
  const op = interpolate(f, [0, 24], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const y = interpolate(f, [0, 26], [50, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) });
  const scan = interpolate(f % 70, [0, 70], [40, 300]);
  return (
    <div
      style={{
        width: 360,
        height: 720,
        borderRadius: 46,
        background: "#0C1626",
        border: `9px solid #1B2A3E`,
        boxShadow: "0 40px 100px rgba(0,0,0,0.5)",
        padding: 22,
        opacity: op,
        translate: `0px ${y}px`,
      }}
    >
      <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 22, color: COLORS.sub, marginBottom: 16 }}>
        Escanear QR
      </div>
      <div
        style={{
          height: 360,
          borderRadius: 22,
          background: "#08111E",
          border: `1px solid ${NEUTRAL.line}`,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="150" height="150" viewBox="0 0 24 24" fill={COLORS.blueLight} opacity={0.9}>
          <path d="M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm10-2h2v2h-2v-2Zm4 0h2v2h-2v-2Zm-4 4h2v2h-2v-2Zm4 0h2v4h-4v-2h2v-2Z" />
        </svg>
        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            top: scan,
            height: 3,
            background: COLORS.greenLight,
            boxShadow: `0 0 18px ${COLORS.greenLight}`,
          }}
        />
      </div>
      <div
        style={{
          marginTop: 18,
          padding: "14px 18px",
          borderRadius: 14,
          background: `${COLORS.greenLight}18`,
          border: `1px solid ${COLORS.greenLight}44`,
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 24,
          color: COLORS.greenLight,
        }}
      >
        VIN reconocido ✓
      </div>
    </div>
  );
};

export const S6Platforms: React.FC = () => {
  return (
    <AbsoluteFill>
      <Backdrop tint="blue" />
      <AbsoluteFill style={{ padding: SAFE, paddingTop: SAFE * 0.7 }}>
        <div style={{ marginBottom: 16 }}>
          <Kicker>Una plataforma, dos frentes</Kicker>
        </div>
        <Title size={74} maxWidth={1500}>
          Portal web para supervisar. App para operar.
        </Title>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 80, marginTop: 60, justifyContent: "center" }}>
          <Laptop delay={18} />
          <ScanPhone delay={30} />
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
